/*
  File: italiansecret-subscription-order-grid.js
  Description:
  Parallel subscription test clone of the Build Your Box order grid logic.
  This file keeps the current one-off grid untouched while testing Prometheus
  B2C selling plan selection and Shopify Ajax Cart selling_plan lines.

  Based on italiansecret-order-grid.js:
  - UberEats-like tabs navigation:
    - Tabs scroll horizontally (CSS), click scrolls to section (anchors)
    - Active tab updates automatically based on scroll position (IntersectionObserver)
  - Tabs are dynamic (reads buttons data-mode from DOM)
    Core: pizzas / gf / toppings / bases
    Extra: type--<handle> (auto from product_type, controlled via liquid blocks ordering too)
  - Pizzas: variant by SKU suffix (01 = Regular, 02 = Gluten Free)
  - Qty controls per card (Add -> [-] n [+])
  - AJAX cart updates via /cart/add.js, /cart/change.js, and /cart/update.js
  - Modal preview on image/title click with media slider
*/

(function () {
  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call(
      (root || document).querySelectorAll(selector)
    );
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    });
  }

  function updateVariantQty(variantId, qty) {
    var payload = { updates: {} };
    payload.updates[variantId] = qty;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart update failed');
      return res.json();
    });
  }

  function cleanProperties(properties) {
    if (!properties || typeof properties !== 'object') return null;
    var cleaned = {};
    Object.keys(properties).forEach(function (key) {
      if (properties[key] !== null && typeof properties[key] !== 'undefined') {
        cleaned[key] = properties[key];
      }
    });
    return Object.keys(cleaned).length ? cleaned : null;
  }

  function addCartLine(variantId, qty, sellingPlanId, properties) {
    var payload = {
      id: Number(variantId),
      quantity: Math.max(1, parseInt(qty, 10) || 1)
    };
    if (sellingPlanId) payload.selling_plan = String(sellingPlanId);
    var cleanedProperties = cleanProperties(properties);
    if (cleanedProperties) payload.properties = cleanedProperties;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart add failed');
      return fetchCart();
    });
  }

  function changeCartLine(lineKey, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: Math.max(0, parseInt(qty, 10) || 0)
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart line change failed');
      return res.json();
    });
  }

  function changeCartLineNumber(lineNumber, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        line: Number(lineNumber),
        quantity: Math.max(0, parseInt(qty, 10) || 0)
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart line change failed');
      return res.json();
    });
  }

  function getQtyInCart(cart, variantId) {
    var idNum = Number(variantId);
    if (!isFinite(idNum)) return 0;
    var q = 0;
    (cart.items || []).forEach(function (item) {
      if (item.variant_id === idNum) q += item.quantity;
    });
    return q;
  }

  function getSellingPlanId(item) {
    if (!item) return '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      return String(item.selling_plan_allocation.selling_plan.id || '').split('/').pop();
    }
    return String(item.selling_plan || item.selling_plan_id || '').split('/').pop();
  }

  function getMatchingCartItem(cart, variantId, sellingPlanId) {
    var idNum = Number(variantId);
    var requestedPlan = String(sellingPlanId || '').split('/').pop();
    if (!isFinite(idNum)) return null;
    return (cart.items || []).find(function (item) {
      var itemPlan = getSellingPlanId(item);
      return item.variant_id === idNum && itemPlan === requestedPlan;
    }) || null;
  }

  function getCartItemsForVariant(cart, variantId) {
    var idNum = Number(variantId);
    if (!isFinite(idNum)) return [];
    return (cart.items || []).filter(function (item) {
      return item.variant_id === idNum;
    });
  }

  function runSequential(items, task) {
    return items.reduce(function (chain, item) {
      return chain.then(function (cart) {
        return task(item, cart);
      });
    }, Promise.resolve(null));
  }

  function formatMoney(cents) {
    var intCents = parseInt(cents, 10);
    if (!isFinite(intCents)) return '';
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(intCents);
    }
    return '\u00a3' + (intCents / 100).toFixed(2);
  }

  function parseCents(value) {
    var cents = parseInt(value, 10);
    return isFinite(cents) ? cents : null;
  }

  function discountedCents(cents, discountPercent) {
    var base = parseCents(cents);
    var percent = Number(discountPercent || 0);
    if (!isFinite(base) || base < 0) return null;
    if (!isFinite(percent) || percent <= 0) return base;
    return Math.max(0, Math.round(base * (100 - Math.min(100, percent)) / 100));
  }

  function normalizePlanId(value) {
    return String(value || '').split('/').pop();
  }

  function getFixedHeaderOffset() {
    // Best-effort offset for sticky headers; safe default.
    // If your theme has a known header height, you can hardcode it here.
    var header = document.querySelector('[data-header], .header, header');
    if (!header) return 12;
    var rect = header.getBoundingClientRect();
    var h = rect && rect.height ? rect.height : 0;
    return Math.round(h) + 12;
  }

  function scrollToSection(sectionEl) {
    if (!sectionEl) return;
    var offset = getFixedHeaderOffset();

    var top =
      window.pageYOffset +
      sectionEl.getBoundingClientRect().top -
      offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth'
    });
  }

  function initOrderGrid(root) {
    if (!root) return;

    var defaultTab = root.getAttribute('data-default-tab') || 'pizzas';
    var showTabs = root.getAttribute('data-show-tabs') === 'true';
    var qtyStep = parseInt(root.getAttribute('data-qty-step'), 10);
    if (!isFinite(qtyStep) || qtyStep < 1) qtyStep = 1;
    var apiBaseUrl = (root.getAttribute('data-prometheus-api-base-url') || '').replace(/\/+$/, '');
    var shopDomain = root.getAttribute('data-shop-domain') || '';
    var storageModeKey = 'italiansecret_subscription_mode';
    var storageOptionKey = 'italiansecret_subscription_option_id';
    var storagePlanKey = 'italiansecret_subscription_selling_plan_id';
    var storageOptionsKey = 'italiansecret_subscription_options';
    var defaultMode = root.getAttribute('data-subscription-mode-default') || 'one_time';
    var purchaseMode = localStorage.getItem(storageModeKey) || defaultMode;
    if (purchaseMode !== 'subscription') purchaseMode = 'one_time';
    var selectedOptionId = localStorage.getItem(storageOptionKey) || '';
    var subscriptionConfig = null;
    var subscriptionConfigLoaded = false;
    var productConfigByVariant = {};

    var tabsWrap = $('[data-is-order-tabs]', root);
    var tabs = $$('.js-order-tab', root);
    var cards = $$('.is-order-card', root);
    var sections = $$('[data-order-section]', root);
    var modeButtons = $$('[data-subscription-mode-button]', root);

    function hideProductionStickyCart() {
      $$('[data-is-sticky-cart-bar]').forEach(function (el) {
        if (el.hasAttribute('data-is-subscription-sticky-cart-bar')) return;
        el.setAttribute('data-is-subscription-hidden-production-bar', 'true');
        el.hidden = true;
        el.style.setProperty('display', 'none', 'important');
      });
    }

    // If tabs are enabled but default tab doesn't exist, fallback to first available
    var availableModes = tabs
      .map(function (b) { return b.getAttribute('data-mode') || ''; })
      .filter(Boolean);

    var currentMode = defaultTab;
    if (showTabs && availableModes.length) {
      if (availableModes.indexOf(currentMode) === -1) currentMode = availableModes[0];
    }

    var busy = false;

    function allVariantIds() {
      var ids = [];
      cards.forEach(function (card) {
        [
          card.getAttribute('data-regular-variant-id'),
          card.getAttribute('data-gf-variant-id'),
          card.getAttribute('data-single-variant-id')
        ].forEach(function (id) {
          if (id && ids.indexOf(id) === -1) ids.push(id);
        });
      });
      return ids;
    }

    function allProductIds() {
      var ids = [];
      cards.forEach(function (card) {
        var id = card.getAttribute('data-product-id');
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    }

    function availableOptions() {
      var optionsById = {};
      Object.keys(productConfigByVariant).forEach(function (variantId) {
        var product = productConfigByVariant[variantId];
        (product.plans || []).forEach(function (plan) {
          (plan.options || []).forEach(function (option) {
            if (!option || !option.available || !option.sellingPlanFormId) return;
            var optionId = String(option.optionId || option.sellingPlanFormId);
            if (!optionsById[optionId]) {
              optionsById[optionId] = option;
            }
          });
        });
      });
      return Object.keys(optionsById).map(function (key) { return optionsById[key]; });
    }

    function sortedAvailableOptions() {
      return availableOptions().sort(function (a, b) {
        var aCount = Number(a.intervalCount || 0);
        var bCount = Number(b.intervalCount || 0);
        var aUnit = String(a.intervalUnit || '');
        var bUnit = String(b.intervalUnit || '');
        if (aUnit !== bUnit) return aUnit.localeCompare(bUnit);
        return aCount - bCount;
      });
    }

    function serializeOptionsForSticky(options) {
      return options.map(function (option) {
        return {
          optionId: String(option.optionId || ''),
          label: String(option.label || option.frequency || ''),
          intervalCount: option.intervalCount,
          intervalUnit: option.intervalUnit,
          frequency: option.frequency,
          discountPercent: Number(option.discountPercent || (option.pricingHint && option.pricingHint.discountPercent) || 0) || 0,
          pricingHint: option.pricingHint || null
        };
      }).filter(function (option) {
        return option.optionId;
      });
    }

    function optionForCard(card) {
      var activeVariantId = card.getAttribute('data-active-variant-id');
      if (!activeVariantId) return null;
      return optionForVariantId(activeVariantId);
    }

    function optionForVariantId(variantId) {
      var product = productConfigByVariant[variantId];
      if (!product) return null;
      var options = [];
      (product.plans || []).forEach(function (plan) {
        (plan.options || []).forEach(function (option) {
          if (option && option.available && option.sellingPlanFormId) options.push(option);
        });
      });
      if (!options.length) return null;
      var selected = options.find(function (option) {
        return String(option.optionId || '') === selectedOptionId;
      });
      return selected || options[0];
    }

    function selectedSellingPlanIdForCard(card) {
      if (purchaseMode !== 'subscription') return '';
      var activeVariantId = card.getAttribute('data-active-variant-id');
      var option = optionForVariantId(activeVariantId);
      return option ? normalizePlanId(option.sellingPlanFormId || option.sellingPlanId) : '';
    }

    function selectedSellingPlanIdForVariant(variantId) {
      if (purchaseMode !== 'subscription') return '';
      var option = optionForVariantId(variantId);
      return option ? normalizePlanId(option.sellingPlanFormId || option.sellingPlanId) : '';
    }

    function targetSellingPlanIdForVariant(variantId) {
      return purchaseMode === 'subscription' ? selectedSellingPlanIdForVariant(variantId) : '';
    }

    function variantCanUseCurrentMode(variantId) {
      return purchaseMode !== 'subscription' || !!selectedSellingPlanIdForVariant(variantId);
    }

    function syncSubscriptionSelection() {
      var options = sortedAvailableOptions();
      if (options.length && (!selectedOptionId || !options.some(function (option) { return String(option.optionId || '') === selectedOptionId; }))) {
        selectedOptionId = String(options[0].optionId || '');
      }
      if (!options.length && purchaseMode === 'subscription') {
        purchaseMode = 'one_time';
      }
      localStorage.setItem(storageModeKey, purchaseMode);
      if (selectedOptionId) localStorage.setItem(storageOptionKey, selectedOptionId);
      var selectedPlanId = '';
      var option = options.find(function (item) {
        return String(item.optionId || '') === selectedOptionId;
      }) || options[0] || null;
      if (option) {
        selectedPlanId = normalizePlanId(option.sellingPlanFormId || option.sellingPlanId);
        localStorage.setItem(storagePlanKey, selectedPlanId);
      } else {
        localStorage.removeItem(storagePlanKey);
      }
      try {
        localStorage.setItem(storageOptionsKey, JSON.stringify(serializeOptionsForSticky(options)));
      } catch (err) {}
      var event = new CustomEvent('italiansecret:subscription-selection-changed', {
        detail: {
          mode: purchaseMode,
          optionId: selectedOptionId,
          sellingPlanId: selectedPlanId,
          option: option,
          options: serializeOptionsForSticky(options)
        }
      });
      document.documentElement.dispatchEvent(event);
      window.dispatchEvent(new CustomEvent('italiansecret:subscription-selection-changed', { detail: event.detail }));
    }

    function renderSubscriptionControls() {
      var options = sortedAvailableOptions();
      if (options.length && (!selectedOptionId || !options.some(function (option) { return String(option.optionId || '') === selectedOptionId; }))) {
        selectedOptionId = String(options[0].optionId || '');
      }
      if (subscriptionConfigLoaded && !options.length && purchaseMode === 'subscription') {
        purchaseMode = 'one_time';
      }
      modeButtons.forEach(function (button) {
        var mode = button.getAttribute('data-subscription-mode-button') || 'one_time';
        var unavailableSubscribe = mode === 'subscription' && subscriptionConfigLoaded && !options.length;
        button.classList.toggle('is-active', mode === purchaseMode);
        button.disabled = unavailableSubscribe;
        button.classList.toggle('is-disabled', unavailableSubscribe);
        button.setAttribute('aria-pressed', mode === purchaseMode ? 'true' : 'false');
        if (unavailableSubscribe) {
          button.setAttribute('title', 'Subscription plans are not ready yet');
        } else {
          button.removeAttribute('title');
        }
      });
      syncSubscriptionSelection();
    }

    function fetchSubscriptionConfig() {
      var variants = allVariantIds();
      var products = allProductIds();
      if (!apiBaseUrl || !shopDomain || (!variants.length && !products.length)) {
        subscriptionConfigLoaded = true;
        renderSubscriptionControls();
        return Promise.resolve(null);
      }
      var params = new URLSearchParams();
      params.set('shop', shopDomain);
      if (variants.length) params.set('variantIds', variants.join(','));
      if (products.length) params.set('productIds', products.join(','));
      return fetch(apiBaseUrl + '/storefront/subscriptions/order-now-widget/config?' + params.toString(), {
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('Prometheus subscription config failed');
        return res.json();
      }).then(function (config) {
        subscriptionConfigLoaded = true;
        subscriptionConfig = config;
        productConfigByVariant = {};
        (config.products || []).forEach(function (product) {
          var variantId = normalizePlanId(product.variantId || product.variantGid);
          if (variantId) productConfigByVariant[variantId] = product;
        });
        renderSubscriptionControls();
        refreshCards();
        return config;
      }).catch(function (err) {
        subscriptionConfigLoaded = true;
        console.warn('Prometheus subscription config unavailable', err);
        renderSubscriptionControls();
        return null;
      });
    }

    function setActiveTab(mode) {
      if (!showTabs) return;
      currentMode = mode;

      tabs.forEach(function (btn) {
        var m = btn.getAttribute('data-mode');
        if (m === mode) btn.classList.add('is-active');
        else btn.classList.remove('is-active');
      });

      // Ensure active tab is visible inside horizontal scroll container
    var active = tabs.find(function (b) {
    return b.classList.contains('is-active');
  });

  if (active && tabsWrap) {
    var left = active.offsetLeft;
    tabsWrap.scrollTo({
      left: Math.max(0, left - 8),
      behavior: 'smooth'
    });
  }
    }

    function computeActiveVariantId(card) {
      var isPizza = card.getAttribute('data-is-pizza') === 'true';
      var cardMode = card.getAttribute('data-card-mode') || '';

      if (isPizza) {
        if (cardMode === 'gf') {
          return card.getAttribute('data-gf-variant-id') || null;
        }
        // default pizza card mode (pizzas): prefer regular
        return (
          card.getAttribute('data-regular-variant-id') ||
          card.getAttribute('data-gf-variant-id') ||
          null
        );
      }

      // Non-pizza (topping/base/other types)
      return card.getAttribute('data-single-variant-id') || null;
    }

    function refreshCardTitle(card) {
      var titleSpan = $('.is-order-card__title-text', card);
      if (!titleSpan) return;

      var baseTitle = card.getAttribute('data-base-title') || '';
      var isPizza = card.getAttribute('data-is-pizza') === 'true';
      var cardMode = card.getAttribute('data-card-mode') || '';

      if (isPizza && cardMode === 'gf') {
        titleSpan.textContent = baseTitle + ' GF';
      } else {
        titleSpan.textContent = baseTitle;
      }
    }

    function refreshCardPrice(card) {
      var priceEl = $('.js-order-price', card);
      if (!priceEl) return;

      var b2bPriceText = card.getAttribute('data-b2b-price-text');
      if (b2bPriceText) {
        priceEl.textContent = b2bPriceText;
        return;
      }

      var isPizza = card.getAttribute('data-is-pizza') === 'true';
      var cardMode = card.getAttribute('data-card-mode') || '';
      var cents = null;

      if (isPizza) {
        if (cardMode === 'gf') {
          if (card.getAttribute('data-gf-price')) {
            cents = parseInt(card.getAttribute('data-gf-price'), 10);
          }
        } else {
          if (card.getAttribute('data-regular-price')) {
            cents = parseInt(card.getAttribute('data-regular-price'), 10);
          } else if (card.getAttribute('data-gf-price')) {
            cents = parseInt(card.getAttribute('data-gf-price'), 10);
          }
        }
      } else {
        if (card.getAttribute('data-single-price')) {
          cents = parseInt(card.getAttribute('data-single-price'), 10);
        }
      }

      if (!isFinite(cents)) {
        priceEl.textContent = '';
        return;
      }

      if (purchaseMode === 'subscription') {
        var option = optionForCard(card);
        var discountPercent = option && option.pricingHint ? Number(option.pricingHint.discountPercent || option.discountPercent || 0) : 0;
        var nextCents = discountedCents(cents, discountPercent);
        if (option && isFinite(nextCents)) {
          priceEl.innerHTML = '<span class="is-subscription-price-prefix">From </span>' + formatMoney(nextCents);
          if (discountPercent > 0) {
            priceEl.innerHTML += ' <span class="is-subscription-price-save">' + discountPercent + '% off</span>';
          }
          return;
        }
      }

      priceEl.textContent = formatMoney(cents);
    }

    function refreshCards() {
      // No filtering/hiding. Only compute active variant + title/price.
      cards.forEach(function (card) {
        var activeVariantId = computeActiveVariantId(card);
        if (activeVariantId) {
          card.setAttribute('data-active-variant-id', activeVariantId);
        } else {
          card.setAttribute('data-active-variant-id', '');
        }

        refreshCardTitle(card);
        refreshCardPrice(card);
      });
    }

    function setCardUIFromQty(card, qty) {
      var addBtn = $('.js-order-add-to-cart', card);
      var qtyBox = $('.is-order-card__qty', card);
      var qtyVal = $('.is-order-card__qty-value', card);

      if (!addBtn || !qtyBox || !qtyVal) return;

      var q = Math.max(0, parseInt(qty, 10) || 0);
      qtyVal.textContent = q;

      if (q === 0) {
        addBtn.classList.remove('hidden');
        qtyBox.classList.add('hidden');
      } else {
        addBtn.classList.add('hidden');
        qtyBox.classList.remove('hidden');
      }
    }

    function setCardsBusy(isBusy) {
      cards.forEach(function (card) {
        card.classList.toggle('is-updating', !!isBusy);
      });
    }

    function removeCartItemsFromSnapshot(cart, predicate) {
      var removals = (cart.items || []).map(function (item, index) {
        return { item: item, line: index + 1 };
      }).filter(function (entry) {
        return predicate(entry.item);
      }).sort(function (a, b) {
        return b.line - a.line;
      });

      return runSequential(removals, function (entry) {
        return changeCartLineNumber(entry.line, 0);
      }).then(function () {
        return fetchCart();
      });
    }

    function setVariantQuantityForMode(cart, variantId, qty) {
      var nextQty = Math.max(0, parseInt(qty, 10) || 0);
      var lines = getCartItemsForVariant(cart, variantId);
      var sellingPlanId = targetSellingPlanIdForVariant(variantId);
      var targetLine = getMatchingCartItem(cart, variantId, sellingPlanId);
      var targetPlanId = normalizePlanId(sellingPlanId);

      if (purchaseMode === 'subscription' && !sellingPlanId) {
        return Promise.resolve(cart);
      }

      if (nextQty <= 0) {
        return removeCartItemsFromSnapshot(cart, function (line) {
          return String(line.variant_id || '') === String(variantId);
        });
      }

      if (targetLine && targetLine.key) {
        return changeCartLine(targetLine.key, nextQty)
          .then(function (nextCart) {
            return removeCartItemsFromSnapshot(nextCart, function (line) {
              return String(line.variant_id || '') === String(variantId) && getSellingPlanId(line) !== targetPlanId;
            });
          });
      }

      var sourceLine = lines[0] || null;
      return addCartLine(variantId, nextQty, sellingPlanId, sourceLine ? sourceLine.properties : null)
        .then(function (nextCart) {
          return removeCartItemsFromSnapshot(nextCart, function (line) {
            return String(line.variant_id || '') === String(variantId) && getSellingPlanId(line) !== targetPlanId;
          });
        });
    }

    function convertCartToCurrentMode(cart) {
      var variantIds = allVariantIds();
      var variantsInCart = [];
      (cart.items || []).forEach(function (item) {
        var variantId = String(item.variant_id || '');
        if (!variantId || variantIds.indexOf(variantId) === -1 || variantsInCart.indexOf(variantId) !== -1) return;
        if (!variantCanUseCurrentMode(variantId)) return;
        variantsInCart.push(variantId);
      });

      if (!variantsInCart.length) return Promise.resolve(cart);

      return variantsInCart.reduce(function (chain, variantId) {
        return chain.then(function (currentCart) {
          var qty = getQtyInCart(currentCart, variantId);
          return setVariantQuantityForMode(currentCart, variantId, qty);
        });
      }, Promise.resolve(cart));
    }

    function syncFromCart() {
      fetchCart()
        .then(function (cart) {
          cards.forEach(function (card) {
            var activeVariantId = card.getAttribute('data-active-variant-id');
            if (!activeVariantId) {
              setCardUIFromQty(card, 0);
              return;
            }
            var q = getQtyInCart(cart, activeVariantId);
            setCardUIFromQty(card, q);
          });

          document.documentElement.dispatchEvent(
            new CustomEvent('is:cart:updated', { detail: { cart: cart } })
          );
        })
        .catch(function (err) {
          console.error('Cart sync failed', err);
        });
    }

    function convertAndSyncCart() {
      if (busy) return;
      busy = true;
      setCardsBusy(true);
      fetchCart()
        .then(convertCartToCurrentMode)
        .then(function (cart) {
          cards.forEach(function (card) {
            var activeVariantId = card.getAttribute('data-active-variant-id');
            setCardUIFromQty(card, activeVariantId ? getQtyInCart(cart, activeVariantId) : 0);
          });
          document.documentElement.dispatchEvent(
            new CustomEvent('is:cart:updated', { detail: { cart: cart } })
          );
        })
        .catch(function (err) {
          console.error('Cart conversion failed', err);
          syncFromCart();
        })
        .finally(function () {
          busy = false;
          setCardsBusy(false);
        });
    }

    // Tabs click => scroll to section
    if (showTabs && tabs.length) {
      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var targetId = btn.getAttribute('data-scroll-target');
          var mode = btn.getAttribute('data-mode') || '';
          if (!targetId) return;

          var sectionEl = document.getElementById(targetId);
          if (!sectionEl) return;

          setActiveTab(mode);
          scrollToSection(sectionEl);
        });
      });
    }

    // Initialize active tab based on default + existing section
    hideProductionStickyCart();
    setActiveTab(currentMode);

    refreshCards();
    fetchSubscriptionConfig().finally(function () {
      syncFromCart();
    });

    modeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var requestedMode = button.getAttribute('data-subscription-mode-button') === 'subscription' ? 'subscription' : 'one_time';
        if (requestedMode === 'subscription' && subscriptionConfigLoaded && !sortedAvailableOptions().length) {
          purchaseMode = 'one_time';
        } else {
          purchaseMode = requestedMode;
        }
        renderSubscriptionControls();
        refreshCards();
        convertAndSyncCart();
      });
    });

    window.addEventListener('italiansecret:subscription-option-requested', function (event) {
      var nextOptionId = event && event.detail ? String(event.detail.optionId || '') : '';
      if (!nextOptionId) return;
      var options = sortedAvailableOptions();
      if (!options.some(function (option) { return String(option.optionId || '') === nextOptionId; })) return;
      selectedOptionId = nextOptionId;
      purchaseMode = 'subscription';
      renderSubscriptionControls();
      refreshCards();
      convertAndSyncCart();
    });

// Deterministic scroll spy (Uber Eats style)
(function initScrollSpyFallback() {
  if (!showTabs || !sections.length) return;

  function getStickyOffset() {
    var headerOffset = getFixedHeaderOffset();
    var tabsWrapRect = tabsWrap ? tabsWrap.getBoundingClientRect() : null;
    var tabsHeight = tabsWrapRect ? tabsWrapRect.height : 0;
    return headerOffset + tabsHeight;
  }

  function onScroll() {
    var offset = getStickyOffset();
    var referenceLine = offset + 1;

    var activeMode = null;

    // Loop from bottom to top.
    for (var i = sections.length - 1; i >= 0; i--) {
      var s = sections[i];
      var rect = s.getBoundingClientRect();
      if (rect.top <= referenceLine) {
        activeMode = s.getAttribute('data-mode') || null;
        break;
      }
    }

    // fallback to first section
    if (!activeMode && sections[0]) {
      activeMode = sections[0].getAttribute('data-mode') || null;
    }

    if (activeMode && activeMode !== currentMode) {
      setActiveTab(activeMode);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  // initial sync
  requestAnimationFrame(onScroll);
})();

    // Add / minus / plus. One-off and subscription are alternate cart states for the same variant.
    root.addEventListener('click', function (e) {
      var minus = e.target.closest('.js-order-qty-minus');
      var plus = e.target.closest('.js-order-qty-plus');
      var add = e.target.closest('.js-order-add-to-cart');

      if (!minus && !plus && !add) return;

      var card = e.target.closest('.is-order-card');
      if (!card) return;

      var activeVariantId = card.getAttribute('data-active-variant-id');
      if (!activeVariantId || busy) return;

      var qtyVal = $('.is-order-card__qty-value', card);
      if (!qtyVal) return;

      e.preventDefault();

      var currentQty = Math.max(0, parseInt(qtyVal.textContent, 10) || 0);
      var nextQty = currentQty;

      if (add) {
        nextQty = currentQty + qtyStep;
      } else if (plus) {
        nextQty = currentQty + qtyStep;
      } else if (minus) {
        nextQty = Math.max(0, currentQty - qtyStep);
      }

      if (nextQty === currentQty) return;

      busy = true;
      card.classList.add('is-updating');
      var sellingPlanId = selectedSellingPlanIdForCard(card);
      if (purchaseMode === 'subscription' && !sellingPlanId) {
        console.warn('No subscription selling plan available for this product. Adding it as one-off.');
      }

      fetchCart()
        .then(function (cart) {
          return setVariantQuantityForMode(cart, activeVariantId, nextQty);
        })
        .then(function (cart) {
          var q = getQtyInCart(cart, activeVariantId);
          setCardUIFromQty(card, q);
          document.documentElement.dispatchEvent(
            new CustomEvent('is:cart:updated', { detail: { cart: cart } })
          );
        })
        .catch(function (err) {
          console.error('Cart update failed', err);
        })
        .finally(function () {
          busy = false;
          card.classList.remove('is-updating');
        });
    });

    window.addEventListener('pageshow', function (ev) {
      var entries =
        performance && performance.getEntriesByType
          ? performance.getEntriesByType('navigation')
          : [];
      var nav = entries && entries[0];
      var fromBFCache = ev.persisted || (nav && nav.type === 'back_forward');

      if (fromBFCache) {
        refreshCards();
        syncFromCart();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var grids = document.querySelectorAll('[data-is-subscription-order-grid]');
    Array.prototype.forEach.call(grids, initOrderGrid);
  });
})();
